import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { tone, sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

// Four pads: color + tone (classic Simon pitches).
const PADS = [
  { id: 0, color: '#43a047', lit: '#a5d6a7', freq: 415 }, // green
  { id: 1, color: '#e53935', lit: '#ef9a9a', freq: 311 }, // red
  { id: 2, color: '#fbc02d', lit: '#fff59d', freq: 252 }, // yellow
  { id: 3, color: '#1e88e5', lit: '#90caf9', freq: 209 }, // blue
];

// Playback speed (ms per step) per level — faster = harder.
const LEVELS = [
  { name: 'Chill', speed: 600 },
  { name: 'Brisk', speed: 420 },
  { name: 'Frantic', speed: 280 },
];
// Reach this round to clear a level.
const CLEAR_ROUND = 8;

const GameSimon: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('simon');
  const [level, setLvl] = useState(prog.level || 1);
  const [seq, setSeq] = useState<number[]>([]);
  const [lit, setLit] = useState<number | null>(null);
  const [phase, setPhase] = useState<'idle' | 'watch' | 'repeat' | 'over' | 'cleared'>('idle');
  const [round, setRound] = useState(0);
  const [best] = useState(getHighScore('simon'));
  const inputIdx = useRef(0);
  const savedRef = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const speed = LEVELS[Math.min(level - 1, LEVELS.length - 1)].speed;

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const flash = useCallback((pad: number, dur: number) => {
    setLit(pad);
    tone(PADS[pad].freq, dur * 0.9, 'sine', 0.2);
    const t = setTimeout(() => setLit(null), dur * 0.7);
    timers.current.push(t);
  }, []);

  const playSequence = useCallback((s: number[]) => {
    setPhase('watch');
    clearTimers();
    s.forEach((pad, i) => {
      const t = setTimeout(() => {
        flash(pad, speed);
        if (i === s.length - 1) {
          const t2 = setTimeout(() => { setPhase('repeat'); inputIdx.current = 0; }, speed);
          timers.current.push(t2);
        }
      }, i * speed);
      timers.current.push(t);
    });
  }, [flash, speed]);

  const nextRound = useCallback((prevSeq: number[]) => {
    const ns = [...prevSeq, Math.floor(Math.random() * 4)];
    setSeq(ns);
    setRound(ns.length);
    const t = setTimeout(() => playSequence(ns), 600);
    timers.current.push(t);
  }, [playSequence]);

  const start = useCallback((lvl: number) => {
    clearTimers();
    setSeq([]);
    setRound(0);
    setLit(null);
    setPhase('idle');
    inputIdx.current = 0;
    savedRef.current = false;
    setLvl(lvl);
    setLevel('simon', lvl);
    const t = setTimeout(() => nextRound([]), 400);
    timers.current.push(t);
  }, [nextRound]);

  // Cleanup on unmount.
  useEffect(() => () => clearTimers(), []);

  const endGame = useCallback((cleared: boolean) => {
    clearTimers();
    setPhase(cleared ? 'cleared' : 'over');
    if (!savedRef.current) {
      savedRef.current = true;
      // Score = rounds reached (the last fully-correct round).
      const reached = cleared ? round : round - 1;
      recordScore('simon', Math.max(0, reached), level);
      if (cleared) { sfx.win(); unlockNext('simon', level); }
      else sfx.lose();
    }
  }, [round, level]);

  const press = (pad: number) => {
    if (phase !== 'repeat') return;
    flash(pad, 260);
    if (seq[inputIdx.current] === pad) {
      inputIdx.current += 1;
      if (inputIdx.current === seq.length) {
        // Completed the round.
        if (seq.length >= CLEAR_ROUND) { endGame(true); return; }
        setPhase('watch');
        sfx.levelUp();
        const t = setTimeout(() => nextRound(seq), 700);
        timers.current.push(t);
      }
    } else {
      endGame(false);
    }
  };

  const statusText = phase === 'watch' ? 'Watch…' : phase === 'repeat' ? 'Your turn!' : phase === 'idle' ? 'Get ready…' : '';

  return (
    <div className="gsimon">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{round}</b><i>round</i></span>
          <span className="gstat"><b>{best}</b><i>best</i></span>
          <span className="gstat"><b>{LEVELS[level - 1].name}</b><i>mode</i></span>
        </div>
        <button className="game-restart" onClick={() => start(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {LEVELS.map((lv, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('simon').unlockedLevel;
          return (
            <button key={lv.name} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && start(lvl)} disabled={!unlocked}>
              {unlocked ? lv.name : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: reach round <b>{challengeScore}</b></div>
      )}

      <div className="gsimon-pad-wrap">
        <div className="gsimon-pads">
          {PADS.map(p => (
            <button
              key={p.id}
              className={`gsimon-pad pad-${p.id} ${lit === p.id ? 'lit' : ''}`}
              style={{ background: lit === p.id ? p.lit : p.color }}
              onClick={() => press(p.id)}
              disabled={phase !== 'repeat'}
              aria-label={`Pad ${p.id + 1}`}
            />
          ))}
        </div>
        <div className="gsimon-center">
          <span className="gsimon-round">{round}</span>
          <span className="gsimon-status">{statusText}</span>
        </div>
      </div>

      {phase === 'idle' && round === 0 && (
        <button className="g-btn primary gsimon-startbtn" onClick={() => start(level)}>▶ Start</button>
      )}

      {(phase === 'over' || phase === 'cleared') && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">{phase === 'cleared' ? '🏆' : '🧠'}</div>
            <h3>{phase === 'cleared' ? `Level cleared — round ${round}!` : 'Wrong pad!'}</h3>
            <p>You reached round <b>{phase === 'cleared' ? round : Math.max(0, round - 1)}</b>.{phase === 'cleared' && level < LEVELS.length ? ' Next speed unlocked.' : ''}</p>
            <div className="go-actions">
              {phase === 'cleared' && level < LEVELS.length && (
                <button className="g-btn primary" onClick={() => start(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => start(level)}>Play again</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Watch the sequence light up, then repeat it. It grows by one each round — reach round {CLEAR_ROUND} to clear.</p>
    </div>
  );
};

export default GameSimon;
